import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { FormInput, IconSelectionModal, VectorIcon } from '@microservices/wiskey-react-components'
import { IconButton } from '@mui/material'
import { ClearIcon } from '@mui/x-date-pickers'

import { VectorIconRenderer } from '@components/VectorIconRenderer'

import { GENERATOR_INPUT_TYPE } from '@constants'

import { ICON_REGISTRY } from '../../constants/iconRegistry'
import { serializeIconToJsx } from '../../helpers/serializeIconToJsx'

type IconSettingsProps = {
  value: string | undefined
  path: string
  onChange: (value: string) => void
  label?: string
  hint?: string
}

export const IconSettings = ({ value, path, onChange, label }: IconSettingsProps) => {
  const { t } = useTranslation()

  const [isModalOpen, setIsModalOpen] = useState(false)

  const matches = value
    ? {
        type: value.match(/type="([^"]+)"/)?.[1],
        val: value.match(/value="([^"]+)"/)?.[1],
        size: value.match(/size={(\d+)}/)?.[1],
        color: value.match(/color="([^"]+)"/)?.[1],
      }
    : {}

  const selectedIcon =
    matches?.type && matches?.val
      ? {
          type: matches.type as any,
          value: matches.val,
          size: Number(matches.size) || 24,
          ...(matches.color ? { color: matches.color } : {}),
        }
      : null

  const handleSave = (nextIcon: VectorIcon | null) => {
    const jsxString = serializeIconToJsx(nextIcon)
    onChange(jsxString)
    setIsModalOpen(false)
  }

  return (
    <>
      <FormInput
        inputType={GENERATOR_INPUT_TYPE.INPUT}
        label={label || t('label.value')}
        name={path}
        placeholder={t('commandParameters.inputs.value.placeholder')}
        readOnly={true}
        value={value}
        additionalBtn={{
          isEnabled: true,
          text: t('commandParameters.buttons.edit'),
          color: 'primary',
          variant: 'contained',
          onClick: () => setIsModalOpen(true),
        }}
        formInputContainerSx={{
          '& .MuiFormLabel-root': { mb: 1 },
          flexWrap: 'nowrap',
          '& .MuiButton-root': { ml: 0.5 },
        }}
        textFieldInputProps={{
          endAdornment: (
            <>
              {selectedIcon && <VectorIconRenderer icon={selectedIcon} />}

              <IconButton size='small' sx={{ width: 24 }} onClick={() => onChange('')}>
                <ClearIcon fontSize='small' />
              </IconButton>
            </>
          ),
        }}
      />

      <IconSelectionModal
        iconRegistry={ICON_REGISTRY}
        isOpen={isModalOpen}
        selectedIcon={selectedIcon}
        showColor={true}
        showSize={true}
        onClose={() => setIsModalOpen(false)}
        onSave={handleSave}
      />
    </>
  )
}
